/**
 * Sibling record overlays (contact sheet + identity warning + merge review)
 * are separate Radix roots. Focus or pointer on the later dialog looks like
 * an "outside" dismiss on the earlier one. That false close retries saves
 * and stacks the warning on top of merge review.
 */

const LAYER_SELECTOR =
  '[data-nynch-blocking-layer], [role="dialog"], [role="alertdialog"]';
const OVERLAY_SELECTOR = '[data-nynch-blocking-overlay]';

export type OverlayDismissEvent = {
  preventDefault: () => void;
  target?: EventTarget | null;
  currentTarget?: EventTarget | null;
  detail?: { originalEvent?: Event };
};

function eventTarget(event: OverlayDismissEvent): EventTarget | null {
  return event.detail?.originalEvent?.target ?? event.target ?? null;
}

function asElement(target: EventTarget | null | undefined): Element | null {
  if (target instanceof Element) return target;
  if (typeof Text !== 'undefined' && target instanceof Text) {
    return target.parentElement;
  }
  return null;
}

/**
 * True when the dismiss event originated on a *different* open dialog/sheet
 * than the one receiving onPointerDownOutside / onFocusOutside.
 */
export function isForeignBlockingLayerEvent(
  event: OverlayDismissEvent,
  currentLayer?: EventTarget | null,
): boolean {
  const clicked = asElement(eventTarget(event));
  if (!clicked) return false;

  const current = asElement(currentLayer ?? event.currentTarget ?? null);

  const otherLayer = clicked.closest(LAYER_SELECTOR);
  if (otherLayer && otherLayer !== current && !current?.contains(otherLayer)) {
    return true;
  }

  const overlay = clicked.closest(OVERLAY_SELECTOR);
  if (!overlay) return false;
  const overlayOwner = overlay.nextElementSibling;
  if (!overlayOwner?.matches(LAYER_SELECTOR)) return false;
  return overlayOwner !== current && !current?.contains(overlayOwner);
}

export function preventForeignLayerDismiss(event: OverlayDismissEvent): boolean {
  if (!isForeignBlockingLayerEvent(event, event.currentTarget ?? null)) {
    return false;
  }
  event.preventDefault();
  return true;
}

export function withForeignLayerDismiss<E extends OverlayDismissEvent>(
  existing?: (event: E) => void,
): (event: E) => void {
  return (event: E) => {
    if (preventForeignLayerDismiss(event)) return;
    existing?.(event);
  };
}
